import React, { useState, useRef, useEffect } from 'react';
import { useLanguage } from '../context/LanguageContext';

const LanguageSwitcher = () => {
  const { language, changeLanguage } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  const languages = [
    { code: 'en', label: 'EN', fullName: 'English' },
    { code: 'bm', label: 'BM', fullName: 'Bahasa Malaysia' },
    { code: 'zh', label: '华语', fullName: '中文' }
  ];

  const currentLang = languages.find(lang => lang.code === language) || languages[0];

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLanguageSelect = (langCode) => {
    changeLanguage(langCode);
    setIsOpen(false);
  };

  return (
    <div className="language-switcher" ref={dropdownRef}>
      <button
        className="language-switcher-btn"
        onClick={() => setIsOpen(!isOpen)}
        aria-label="Select Language"
        aria-expanded={isOpen}
      >
        <i className="fas fa-globe"></i>
        <span className="current-lang">{currentLang.label}</span>
      </button>

      {isOpen && (
        <div className="language-dropdown-wrapper">
          <div className="language-dropdown-inner">
            {languages.map((lang) => (
              <button
                key={lang.code}
                className={`language-option ${language === lang.code ? 'active' : ''}`}
                onClick={() => handleLanguageSelect(lang.code)}
              >
                <span className="lang-code">{lang.label}</span>
                <span className="lang-name">{lang.fullName}</span>
                {language === lang.code && <i className="fas fa-check"></i>}
              </button>
            ))}
          </div>
        </div>
      )}

      <style>{`
        .language-switcher {
          position: relative;
          margin-right: 8px;
        }

        .language-switcher-btn {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.4rem;
          background: transparent;
          border: none;
          color: var(--text-primary);
          width: 50px;
          height: 100%;
          border-radius: 0;
          cursor: pointer;
          transition: all 0.2s ease;
          padding: 0;
          font-size: 0.85rem;
          font-weight: 500;
        }

        .language-switcher-btn i.fa-globe {
          font-size: 1.2rem;
        }

        .current-lang {
          font-size: 0.75rem;
          font-weight: 600;
        }

        /* Dark theme language toggle */
        [data-theme="dark"] .language-switcher-btn {
          color: #929292;
        }

        [data-theme="dark"] .language-switcher-btn:hover {
          color: #ff4655;
        }

        [data-theme="dark"] .language-switcher-btn:hover i {
          transform: scale(1.1);
          color: #ff4655;
        }

        /* Light theme language toggle */
        [data-theme="light"] .language-switcher-btn {
          color: #1a1a2e;
        }

        [data-theme="light"] .language-switcher-btn:hover {
          color: #dc2626;
        }

        [data-theme="light"] .language-switcher-btn:hover i {
          transform: scale(1.1);
          color: #dc2626;
        }

        [data-theme="light"] .language-switcher-btn:hover i {
          transform: scale(1.1);
        }

        /* Outer wrapper - transparent panel */
        .language-dropdown-wrapper {
          position: absolute;
          top: calc(100% + 10px);
          right: 0;
          min-width: 260px;
          padding: 8px;
          border-radius: 16px;
          backdrop-filter: blur(20px);
          -webkit-backdrop-filter: blur(20px);
          box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
          z-index: 1000;
          animation: fadeInDown 0.2s ease;
        }

        [data-theme="dark"] .language-dropdown-wrapper {
          background: rgba(26, 26, 46, 0.3);
          border: 1px solid rgba(255, 255, 255, 0.15);
        }

        [data-theme="light"] .language-dropdown-wrapper {
          background: rgba(255, 255, 255, 0.3);
          border: 1px solid rgba(0, 0, 0, 0.15);
        }

        /* Inner content box - enhanced background */
        .language-dropdown-inner {
          display: flex;
          flex-direction: column;
          gap: 0.3rem;
          padding: 0.8rem;
          border-radius: 12px;
          backdrop-filter: blur(10px);
          -webkit-backdrop-filter: blur(10px);
        }

        [data-theme="dark"] .language-dropdown-inner {
          background: rgba(26, 26, 46, 0.92);
          border: 1px solid rgba(255, 255, 255, 0.08);
        }

        [data-theme="light"] .language-dropdown-inner {
          background: rgba(255, 255, 255, 0.92);
          border: 1px solid rgba(0, 0, 0, 0.08);
        }

        @keyframes fadeInDown {
          from {
            opacity: 0;
            transform: translateY(-10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .language-option {
          width: 100%;
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 0.75rem 1rem;
          background: transparent;
          border: none;
          color: var(--text-primary);
          cursor: pointer;
          transition: all 0.2s ease;
          text-align: left;
          gap: 0.75rem;
          border-radius: 10px;
        }

        [data-theme="dark"] .language-option {
          color: #ced4da;
        }

        [data-theme="dark"] .language-option:hover,
        [data-theme="dark"] .language-option.active {
          background: rgba(255, 70, 85, 0.1);
          color: #ff4655;
        }

        [data-theme="light"] .language-option {
          color: #6b7280;
        }

        [data-theme="light"] .language-option:hover,
        [data-theme="light"] .language-option.active {
          background: rgba(220, 38, 38, 0.1);
          color: #dc2626;
        }

        .lang-code {
          font-weight: 600;
          min-width: 40px;
        }

        .lang-name {
          flex: 1;
          font-size: 0.9rem;
          opacity: 0.9;
        }

        .language-option .fa-check {
          font-size: 0.85rem;
        }

        [data-theme="dark"] .language-option .fa-check {
          color: #ff4655;
        }

        [data-theme="light"] .language-option .fa-check {
          color: #dc2626;
        }
      `}</style>
    </div>
  );
};

export default LanguageSwitcher;
